import React, { useState, useEffect } from 'react';
import Navbar from '../components/NavBar';
import ClubCard from '../components/ClubCard';
import { getMyClubs, leaveClub } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DotLoader } from 'react-spinners';
import { Heart, Users, TrendingUp } from 'lucide-react';

function MyClubs() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchMyClubs();
  }, [isAuthenticated, navigate]);

  const fetchMyClubs = async () => {
    try {
      const data = await getMyClubs();
      setClubs(data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors du chargement des clubs:', error);
      setLoading(false);
    }
  };

  const handleLeaveClub = async (clubId) => {
    const confirmed = window.confirm('Êtes-vous sûr de vouloir quitter ce club ?');
    if (!confirmed) return;

    try {
      await leaveClub(clubId);
      fetchMyClubs(); // Recharger les clubs
    } catch (error) {
      console.error('Erreur lors de la sortie du club:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sortie');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Navbar />
        <div className="flex items-center justify-center h-64">
          <DotLoader color="#6366f1" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <Heart className="text-indigo-500 fill-indigo-500" size={32} />
            Mes Clubs
          </h2>
          <p className="text-gray-400">Les clubs dont vous êtes membre</p>
        </div>

        {/* Statistiques */}
        {clubs.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 hover:border-indigo-500 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Clubs rejoints</p>
                  <p className="text-3xl font-bold text-indigo-500">{clubs.length}</p>
                </div>
                <Heart size={40} className="text-indigo-900/30 fill-indigo-900/30" />
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 hover:border-purple-500 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Membres totaux</p>
                  <p className="text-3xl font-bold text-purple-500">
                    {clubs.reduce((sum, club) => sum + (club.nombreMembres || 0), 0)}
                  </p>
                </div>
                <Users size={40} className="text-purple-900/30" />
              </div>
            </div>

            <div className="bg-slate-900 border border-slate-800 rounded-xl shadow-lg p-4 sm:p-6 hover:border-green-500 transition-all">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-400">Séances/semaine</p>
                  <p className="text-3xl font-bold text-green-500">
                    {clubs.reduce((sum, club) => sum + (club.nombreSeances || 0), 0)}
                  </p>
                </div>
                <TrendingUp size={40} className="text-green-900/30" />
              </div>
            </div>
          </div>
        )}

        {clubs.length > 0 ? (
          <div className="space-y-6">
            {clubs.map(club => (
              <ClubCard 
                key={club._id} 
                club={club} 
                onJoin={() => {}}
                onLeave={handleLeaveClub}
                isMember={true}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-xl p-8 sm:p-12 max-w-md mx-auto">
              <div className="bg-indigo-900/30 border border-indigo-500 w-20 h-20 sm:w-24 sm:h-24 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={40} className="text-indigo-500" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
                Aucun club rejoint
              </h3>
              <p className="text-gray-400 mb-6">
                Vous n'avez rejoint aucun club pour le moment. Découvrez des clubs qui correspondent à vos objectifs !
              </p>
              <button
                onClick={() => navigate('/')}
                className="bg-indigo-500 hover:bg-indigo-600 text-white px-6 py-3 rounded-lg transition font-semibold shadow-lg shadow-indigo-500/20"
              >
                Découvrir des clubs
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

export default MyClubs;